import { zodResolver } from '@hookform/resolvers/zod'
import { useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom'
import { z } from 'zod'

import { apiErrorMessage } from '@/api/client'
import { auth as authApi } from '@/api/endpoints'
import { Alert, Checkbox, Field, Input, Spinner } from '@/components/ui'
import { LANGUAGES, setLanguage } from '@/i18n'
import { useAuth } from '@/store/auth'

const schema = z.object({
  email: z.string().email(),
  password: z.string().min(1),
  remember: z.boolean().optional(),
})
type FormValues = z.infer<typeof schema>

const DEFAULT_LOGO = '/images/learn2ski-logo.png'

export default function LoginPage() {
  const { t, i18n } = useTranslation()
  const { data: branding, isError: brandingFailed } = useQuery({
    queryKey: ['branding'],
    queryFn: authApi.branding,
    staleTime: Infinity,
    retry: false,
  })

  // Ikona a názov záložky podľa školy už na prihlásení.
  useEffect(() => {
    if (branding?.system_name) document.title = branding.system_name
    if (!branding?.favicon_url) return
    const link = document.querySelector<HTMLLinkElement>("link[rel='icon']")
    if (link) {
      link.type = 'image/x-icon'
      link.href = branding.favicon_url
    }
  }, [branding])
  const navigate = useNavigate()
  const location = useLocation() as { state?: { from?: string } }
  const login = useAuth((s) => s.login)
  const completeMfa = useAuth((s) => s.completeMfa)
  const status = useAuth((s) => s.status)
  const [error, setError] = useState<string | null>(null)
  // Druhý krok so zapnutým MFA: heslo prešlo, server čaká na kód.
  const [challenge, setChallenge] = useState<string | null>(null)
  const [code, setCode] = useState('')
  const [verifying, setVerifying] = useState(false)

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { remember: false },
  })

  if (status === 'authenticated') {
    return <Navigate to={location.state?.from ?? '/calendar'} replace />
  }

  async function onSubmit(values: FormValues) {
    setError(null)
    try {
      const mfaChallenge = await login(values.email, values.password, values.remember ?? false)
      if (mfaChallenge) {
        setChallenge(mfaChallenge)
        setCode('')
        return
      }
      navigate(location.state?.from ?? '/calendar', { replace: true })
    } catch (err) {
      setError(apiErrorMessage(err, t('auth.invalidCredentials')))
    }
  }

  async function onCode(event: React.FormEvent) {
    event.preventDefault()
    if (!challenge) return
    setError(null)
    setVerifying(true)
    try {
      await completeMfa(challenge, code)
      navigate(location.state?.from ?? '/calendar', { replace: true })
    } catch (err) {
      // Vypršaná výzva alebo priveľa pokusov - späť na heslo.
      const expired =
        (err as { response?: { data?: { code?: string } } })?.response?.data?.code ===
        'mfa_expired'
      if (expired) setChallenge(null)
      setError(apiErrorMessage(err, t('auth.mfaInvalid')))
    } finally {
      setVerifying(false)
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-100 p-4">
      <div className="card w-full max-w-sm p-6">
        {/* Logo školy (od balíčka Plus), inak Learn2Ski. Kým sa nevie,
            ktoré, je miesto prázdne - aby logo Learn2Ski neprebliklo. */}
        <div className="mb-5 h-11">
          {branding || brandingFailed ? (
            <img
              src={
                !branding || branding.logo_url === DEFAULT_LOGO
                  ? '/images/learn2ski-logo.svg'
                  : branding.logo_url
              }
              alt={branding?.system_name || 'Learn2Ski.me'}
              className="h-11 w-auto max-w-full object-contain"
            />
          ) : null}
        </div>
        <h1 className="mb-5 text-lg font-semibold">{t('auth.signIn')}</h1>

        {error ? (
          <div className="mb-4">
            <Alert variant="error">{error}</Alert>
          </div>
        ) : null}

        {challenge ? (
          <form onSubmit={onCode} className="space-y-3">
            <p className="text-sm text-slate-600">{t('auth.mfaPrompt')}</p>
            <Field label={t('auth.mfaCode')}>
              <Input
                autoFocus
                inputMode="text"
                autoComplete="one-time-code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="123 456"
              />
            </Field>
            <p className="text-xs text-slate-500">{t('auth.mfaRecoveryHint')}</p>
            <button
              type="submit"
              className="btn-primary w-full justify-center"
              disabled={verifying || code.trim().length < 6}
            >
              {verifying ? <Spinner /> : null}
              {t('auth.mfaSubmit')}
            </button>
            <button
              type="button"
              className="w-full text-center text-sm text-slate-500 hover:underline"
              onClick={() => {
                setChallenge(null)
                setError(null)
              }}
            >
              {t('auth.mfaBack')}
            </button>
          </form>
        ) : (
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-3">
          <Field
            label={t('auth.email')}
            error={errors.email ? t('errors.invalidEmail') : undefined}
          >
            <Input type="email" autoComplete="username" autoFocus {...register('email')} />
          </Field>

          <Field
            label={t('auth.password')}
            error={errors.password ? t('errors.required') : undefined}
          >
            <Input type="password" autoComplete="current-password" {...register('password')} />
          </Field>

          {/* Bez zaškrtnutia platí prihlásenie do zatvorenia prehliadača -
              na spoločnom počítači na recepcii je to to, čo chceme. */}
          <Checkbox label={t('auth.rememberMe')} {...register('remember')} />

          <button type="submit" className="btn-primary w-full justify-center" disabled={isSubmitting}>
            {isSubmitting ? <Spinner /> : null}
            {t('auth.submit')}
          </button>
        </form>
        )}

        <Link
          to="/forgot-password"
          className="mt-4 block text-center text-sm text-brand-600 hover:underline"
        >
          {t('auth.forgot')}
        </Link>

        {/* Signing in is the one screen where the language has to be
            switchable before there is a session to read it from. */}
        <div className="mt-5 flex justify-center gap-2 border-t border-slate-200 pt-3">
          {LANGUAGES.map((lang) => (
            <button
              key={lang}
              type="button"
              onClick={() => setLanguage(lang)}
              className={
                i18n.language === lang
                  ? 'text-xs font-semibold uppercase text-brand-600'
                  : 'text-xs uppercase text-slate-400 hover:text-slate-700'
              }
            >
              {lang}
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}
